import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Box from '@material-ui/core/Box';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import DataListPagination from './DataListPagination';
import DataListSelectionBar from './DataListSelectionBar';
import DataListSelectionCheckbox from './DataListSelectionCheckbox';
import DataListToolbar from './DataListToolbar';
import DataSourceProvider from '../DataSource/DataSourceProvider';
import FilterSelection from '../DataSource/FilterSelection';
import ViewSelection from '../DataSource/ViewSelection';
import arrayFromNumber from '@afosto/utils/array/arrayFromNumber';

const DataList = ({
  dataSource,
  renderItem,
  renderSkeletonItem,
  component: Component,
  emptyMessage,
  emptyMessageAlignment,
  filterFormatters,
  filterRenderers,
  bulkActions,
  PaginationComponent,
  SelectionBarComponent,
  viewPagination,
  ...otherProps
}) => {
  const intl = useIntl();
  const {
    data,
    hasModule,
    isLoadingData,
    filterSelection,
    pagination = {},
    selection: selectionModule = {},
  } = dataSource;
  const { itemsPerPage = 5 } = pagination;
  const { allItemsSelected = false, selectedItems = [] } = selectionModule;
  const showSelectionBar = (allItemsSelected || selectedItems.length > 0) && SelectionBarComponent;

  if (isLoadingData && renderSkeletonItem) {
    return (
      <DataSourceProvider value={dataSource}>
        {(hasModule('views') || !!filterSelection) && <DataListToolbar />}
        <Component>{arrayFromNumber(itemsPerPage).map(n => renderSkeletonItem(n))}</Component>
        {hasModule('views') && <ViewSelection />}
        {hasModule('filters') && <FilterSelection />}
      </DataSourceProvider>
    );
  }

  return (
    <DataSourceProvider value={dataSource}>
      {(hasModule('views') || !!filterSelection) && <DataListToolbar />}
      {showSelectionBar && (
        <Hidden smDown implementation="js">
          <SelectionBarComponent bulkActions={bulkActions} position="relative" />
        </Hidden>
      )}
      <Component {...otherProps}>
        {data.map(item => {
          const SelectionCheckbox = hasModule('selection')
            ? () => <DataListSelectionCheckbox item={item} />
            : undefined;
          return renderItem(item, { SelectionComponent: SelectionCheckbox });
        })}
        {data.length === 0 && (
          <Box textAlign={emptyMessageAlignment} p={2}>
            <Typography variant="body2">
              {emptyMessage ||
                intl.formatMessage({
                  id: 'dataList.emptyMessage',
                  defaultMessage: 'No items',
                  description: 'The default empty message of the data list component',
                })}
            </Typography>
          </Box>
        )}
      </Component>
      {showSelectionBar && (
        <Hidden mdUp implementation="js">
          <SelectionBarComponent bulkActions={bulkActions} />
        </Hidden>
      )}
      {hasModule('filters') && (
        <FilterSelection filterFormatters={filterFormatters} filterRenderers={filterRenderers} />
      )}
      {hasModule('views') && <ViewSelection />}
      {hasModule('pagination') && viewPagination && <PaginationComponent />}
    </DataSourceProvider>
  );
};

DataList.propTypes = {
  /**
   * A dataSource context created with UseDataSource. Items will be loaded from this source.
   */
  dataSource: PropTypes.object.isRequired,
  /**
   * Function that renders an item of the list.
   * This is required because the data properties can be different for each use case.
   */
  renderItem: PropTypes.func.isRequired,
  /**
   * Function that renders the skeleton item.
   */
  renderSkeletonItem: PropTypes.func,
  /**
   * Component to use as the base of the list.
   */
  component: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  PaginationComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
   * Component to use as the selection bar.
   */
  SelectionBarComponent: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
  /**
   * Empty message.
   */
  emptyMessage: PropTypes.string,
  /**
   * The alignment of the empty message.
   */
  emptyMessageAlignment: PropTypes.string,
  /**
   * The custom filter formatters.
   */
  filterFormatters: PropTypes.object,
  /**
   * The custom filter renderers.
   */
  filterRenderers: PropTypes.object,
  /**
   * Bulk actions array.
   */
  bulkActions: PropTypes.array,
  /**
   * Boolean controlling if the pagination should be rendered.
   */
  viewPagination: PropTypes.bool,
};

DataList.defaultProps = {
  renderSkeletonItem: undefined,
  component: List,
  PaginationComponent: DataListPagination,
  SelectionBarComponent: DataListSelectionBar,
  emptyMessage: undefined,
  emptyMessageAlignment: 'center',
  filterFormatters: {},
  filterRenderers: {},
  bulkActions: [],
  viewPagination: true,
};

export default DataList;
